import React, { useMemo } from "react"
import { Badge, Calendar } from "@mimber/ui-web"
import { generateOccurrenceDates } from "@mimber/schema"
import type { Dayjs } from "dayjs"
import type { ScheduledActionCalendarPreviewProps } from "./ScheduledActionCalendarPreview.types"

const toDateKey = (iso: string) => {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`
}

export const ScheduledActionCalendarPreview: React.FunctionComponent<
  ScheduledActionCalendarPreviewProps
> = ({
  scheduledFor,
  startsAt,
  isRecurring,
  intervalLength,
  intervalType,
  endsAt,
  maxOccurrences,
  executionCount = 0,
  daysOfWeek,
  dayOfMonth,
  weekIndex,
  monthOfYear,
}) => {
  const { pastDateSet, upcomingDateSet } = useMemo(() => {
    if (!scheduledFor) {
      return {
        pastDateSet: new Set<string>(),
        upcomingDateSet: new Set<string>(),
      }
    }

    if (!isRecurring || !intervalLength || !intervalType) {
      return {
        pastDateSet: new Set<string>(),
        upcomingDateSet: new Set([toDateKey(scheduledFor)]),
      }
    }

    const calendarFields = { daysOfWeek, dayOfMonth, weekIndex, monthOfYear }

    // `scheduledFor` only reflects the *next* pending send — for recurring
    // schedules it advances forward every time an occurrence completes. To
    // show completed sends we replay history from the original `startsAt`
    // for exactly `executionCount` occurrences.
    const pastDates =
      startsAt && executionCount > 0
        ? generateOccurrenceDates(startsAt, intervalLength, intervalType, {
            maxOccurrences: executionCount,
            executionCount: 0,
            ...calendarFields,
          })
        : []

    const upcomingDates = generateOccurrenceDates(
      scheduledFor,
      intervalLength,
      intervalType,
      {
        endsAt,
        maxOccurrences,
        executionCount,
        ...calendarFields,
      }
    )

    return {
      pastDateSet: new Set(pastDates.map(toDateKey)),
      upcomingDateSet: new Set(upcomingDates.map(toDateKey)),
    }
  }, [
    scheduledFor,
    startsAt,
    isRecurring,
    intervalLength,
    intervalType,
    endsAt,
    maxOccurrences,
    executionCount,
    daysOfWeek,
    dayOfMonth,
    weekIndex,
    monthOfYear,
  ])

  const cellRender = (current: Dayjs) => {
    const dateKey = current.format("YYYY-MM-DD")
    if (upcomingDateSet.has(dateKey)) {
      return <Badge status="processing" text="" />
    }
    if (pastDateSet.has(dateKey)) {
      return <Badge status="success" text="" />
    }
    return null
  }

  return <Calendar fullscreen={false} cellRender={cellRender} />
}
